<template>
  <div class="device-group-action-bar">
    <div
      v-if="selectedDevices.length > 0"
      class="device-group-action-bar__box d-flex flex-wrap align-items-center gap-2"
    >
      <span class="fw-semibold me-1">
        {{
          $t("device_groups.selected_summary", {
            count: selectedDevices.length,
            power: totalPowerKw(selectedDevices),
          })
        }}
      </span>

      <div v-if="viewedGroup === null" ref="menuRoot" class="position-relative">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="busy"
          aria-haspopup="menu"
          :aria-expanded="menuOpen"
          @click="menuOpen = !menuOpen"
        >
          {{ $t("device_groups.add_to_group") }}
          <FontAwesomeIcon icon="chevron-down" class="ms-1" />
        </button>
        <div
          v-if="menuOpen"
          class="dropdown-menu show device-group-action-bar__menu"
          role="menu"
          @keydown.esc="menuOpen = false"
        >
          <button
            v-for="group in groups"
            :key="group.id"
            type="button"
            role="menuitem"
            class="dropdown-item d-flex justify-content-between gap-3"
            @click="choose(() => $emit('addToGroup', group.id))"
          >
            <span class="text-truncate">{{ group.name }}</span>
            <span class="text-body-secondary">{{
              countByGroup[group.id] ?? 0
            }}</span>
          </button>
          <div v-if="groups.length > 0" class="dropdown-divider"></div>
          <button
            type="button"
            role="menuitem"
            class="dropdown-item"
            @click="choose(() => $emit('newGroupFromSelection'))"
          >
            <FontAwesomeIcon icon="plus" class="me-1" />
            {{ $t("device_groups.new_group_from_selection") }}
          </button>
        </div>
      </div>
      <button
        v-else
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="busy"
        @click="$emit('removeFromGroup', viewedGroup.id)"
      >
        {{ $t("device_groups.remove_from_group") }}
      </button>

      <button
        type="button"
        class="btn btn-link btn-sm ms-auto"
        @click="$emit('clearSelection')"
      >
        {{ $t("device_groups.clear_selection") }}
      </button>
    </div>

    <div
      v-else
      class="d-flex align-items-center gap-2 text-body-secondary fs-9 device-group-action-bar__hint"
    >
      <span>
        {{
          viewedGroup === null
            ? $t("device_groups.hint_select_ungrouped")
            : $t("device_groups.hint_select_to_remove")
        }}
      </span>
      <button
        v-if="viewedGroup"
        type="button"
        class="btn btn-link btn-sm ms-auto"
        @click="$emit('editGroup', viewedGroup)"
      >
        {{ $t("device_groups.edit_group") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  totalPowerKw,
  type DeviceCountsByGroup,
} from "@/dashboard/production_strategy/device_groups/deviceGroupUtils";
import type { DeviceGroup } from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";
import type { RegistryDevice } from "@/types";
import { onClickOutside } from "@vueuse/core";
import { ref, useTemplateRef } from "vue";

/**
 * The bar above the Devices tab table. Outside a group's view (All and Ungrouped) it adds the selected
 * (ungrouped) devices to a group or starts a new one from them; in a group's view it removes them
 * from that group.
 */
withDefaults(
  defineProps<{
    /** Each group's members among the devices the tab shows, for the menu. */
    countByGroup: DeviceCountsByGroup;
    selectedDevices: RegistryDevice[];
    /** The groups the add-to menu offers. */
    groups: DeviceGroup[];
    /** The group whose chip is viewed, or null in All and Ungrouped. */
    viewedGroup: DeviceGroup | null;
    busy?: boolean;
  }>(),
  { busy: false }
);

defineEmits<{
  addToGroup: [groupId: number];
  newGroupFromSelection: [];
  removeFromGroup: [groupId: number];
  clearSelection: [];
  editGroup: [group: DeviceGroup];
}>();

const menuOpen = ref(false);
const menuRoot = useTemplateRef<HTMLElement>("menuRoot");

onClickOutside(menuRoot, () => {
  menuOpen.value = false;
});

const choose = (action: () => void) => {
  menuOpen.value = false;
  action();
};
</script>

<style scoped lang="scss">
.device-group-action-bar {
  min-height: 48px;
  margin-bottom: 8px;
}

.device-group-action-bar__box {
  padding: 6px 6px 6px 12px;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 8px;
  font-size: 0.8rem;
}

.device-group-action-bar__hint {
  min-height: 48px;
}

.device-group-action-bar__menu {
  top: calc(100% + 4px);
  left: 0;
  width: 300px;
  max-height: 320px;
  overflow-y: auto;
}
</style>
